import { ChangeDetectionStrategy, Component, Input } from "@angular/core";

import {
  Bloque,
  BloqueHechos,
  BloqueLista,
  BloqueTabla,
  BloqueTexto,
  EstadoBloque,
} from "../../shared/model/iso42001/homologacion-modelo.model";

const ESTADOS: EstadoBloque[] = ["ok", "falla", "aviso", "neutro"];

const ESTADO_TEXTO: Record<EstadoBloque, string> = {
  ok: "Cumple",
  falla: "Falla",
  aviso: "Aviso",
  neutro: "—",
};

/**
 * Pinta un bloque de presentación del archivo de homologación: hechos,
 * tabla, lista o texto. Es el ÚNICO componente que pinta contenido del
 * archivo.
 *
 * Ese contenido lo escribió un modelo (notas, justificaciones, fragmentos
 * de bitácoras): va SOLO por interpolación, que escapa. Aquí no se inyecta
 * HTML (ni binding de HTML crudo ni rodeos del sanitizador de Angular), y
 * no debe hacerse nunca: sería un XSS con la sesión de quien abre la
 * pantalla. La guardia del plan (Task 21) lo revisa con grep.
 *
 * El archivo no es de fiar en la forma tampoco: todo lo que se recorre se
 * revisa que sea lista, y un tipo desconocido se avisa sin romper nada.
 */
@Component({
  selector: "app-bloque-evidencia",
  standalone: true,
  templateUrl: "./bloque-evidencia.component.html",
  styleUrls: ["./bloque-evidencia.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BloqueEvidenciaComponent {
  @Input() bloque: Bloque | null = null;

  get tipo(): string {
    return typeof this.bloque?.tipo === "string" ? this.bloque.tipo : "";
  }

  get titulo(): string {
    return typeof this.bloque?.titulo === "string" ? this.bloque.titulo : "";
  }

  get hechos(): BloqueHechos["items"] {
    const items = (this.bloque as BloqueHechos)?.items;
    return Array.isArray(items) ? items.filter((i) => i && typeof i === "object") : [];
  }

  get columnas(): string[] {
    const c = (this.bloque as BloqueTabla)?.columnas;
    return Array.isArray(c) ? c : [];
  }

  get filas(): string[][] {
    const f = (this.bloque as BloqueTabla)?.filas;
    return Array.isArray(f) ? f.map((fila) => (Array.isArray(fila) ? fila : [String(fila)])) : [];
  }

  get lista(): string[] {
    const i = (this.bloque as BloqueLista)?.items;
    return Array.isArray(i) ? i : [];
  }

  get texto(): string {
    const t = (this.bloque as BloqueTexto)?.texto;
    return t == null ? "" : String(t);
  }

  /** Solo los cuatro estados del contrato; cualquier otra cosa es neutro. */
  estado(valor: unknown): EstadoBloque | null {
    return ESTADOS.includes(valor as EstadoBloque) ? (valor as EstadoBloque) : null;
  }

  estadoFila(i: number): EstadoBloque | null {
    const estados = (this.bloque as BloqueTabla)?.estados;
    return Array.isArray(estados) ? this.estado(estados[i]) : null;
  }

  estadoTexto(valor: unknown): string {
    const e = this.estado(valor);
    return e ? ESTADO_TEXTO[e] : "";
  }
}
